import { useLayoutEffect, useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import s from './MasterDetail.module.scss'

/**
 * Was unter dem Rahmen bleibt, wenn sich nichts messen lässt: der untere
 * Seitenrand ($pad-page-desktop).
 */
const UNTEN = 64

/**
 * Zwei Bereiche nebeneinander, die in sich scrollen: links die Liste, rechts
 * das gewählte Stück.
 *
 * DIE HÖHE WIRD GEMESSEN, nicht gerechnet: der Bildschirm abzüglich dessen,
 * was über dem Rahmen steht — Kopfzeile, Filterleiste, Ränder — und dessen,
 * was darunter noch kommt, etwa die Notiz unter den Meldungen. Eine feste
 * Zahl stimmte nur für EINE Filterleiste; bricht sie in zwei Zeilen um, wie
 * bei den Ereignissen, scrollte die Seite um den Rest. Gemessen wird beim
 * Einhängen und immer, wenn sich darum etwas ändert (Fenstergröße, ein Feld
 * mehr in der Leiste). Auf dem Tablet und am Handy stehen die Bereiche
 * untereinander, und die Seite scrollt wie jede andere. Die Breite der Liste
 * gibt die Seite vor: Meldungen brauchen weniger als Termine mit ihren Chips.
 */
export function MasterDetail({ liste, detail, breite = '400px' }: {
  liste: ReactNode
  detail: ReactNode
  /** Die Spalte der Liste als CSS-Breite, etwa `400px` oder `minmax(360px, 36%)`. */
  breite?: string
}) {
  const rahmen = useRef<HTMLDivElement>(null)
  const [oben, setOben] = useState<number | null>(null)

  useLayoutEffect(() => {
    const el = rahmen.current
    if (!el) return
    const messen = () => {
      const eigen = el.getBoundingClientRect()
      // Der Inhaltsbereich (`main`) endet mit seinem unteren Rand; was
      // zwischen dem Rahmen und diesem Ende liegt, gehört nicht zum Rahmen.
      const inhalt = el.closest('main')?.getBoundingClientRect()
      const unten = inhalt ? Math.round(inhalt.bottom - eigen.bottom) : UNTEN
      setOben(Math.round(eigen.top + window.scrollY) + Math.max(unten, 0))
    }
    messen()
    // Die Leiste darüber ändert ihre Höhe, wenn sie umbricht — der Beobachter
    // auf dem Elternteil sieht das; das Fenster den Rest.
    const beobachter = new ResizeObserver(messen)
    if (el.parentElement) beobachter.observe(el.parentElement)
    window.addEventListener('resize', messen)
    return () => {
      beobachter.disconnect()
      window.removeEventListener('resize', messen)
    }
  }, [])

  const stil = {
    '--liste': breite,
    ...(oben !== null ? { '--oben': `${oben}px` } : {}),
  } as CSSProperties
  return (
    <div className={s.seite} style={stil} ref={rahmen}>
      <div className={s.links}>{liste}</div>
      <div className={s.rechts}>{detail}</div>
    </div>
  )
}
